// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux
import { get, isEmpty } from 'lodash';

import { sendCreateCustomChemical, fetchBrandsFromSprout } from 'products/loaders';

// selectors
import {
  getActiveUserId,
  getActiveScriptType,
  getActiveFieldCountryCode,
  getActiveCropDfpUuid
} from 'current/selectors';
import { getUserDetails } from 'user/selectors';
import { 
  setProductsForScript,
  setProtectionProductForScript,
  setFertilizerProductForScript,
  setFertilizerProductInput
} from '../current/uiwidgets';
import { setProductInput } from '../current/uiwidgets/product-inputs/index';

// actions
import { treatErrorAs } from 'api/errors';

// constants
import { errorLevel } from 'constants/errors';
import { applicationTypes, applicationSubTypes, scriptTypes } from 'constants/scripts';

// finders
import {
  findFertilizersForSubstanceTypeUuid,
  findProtectionsForChemicalTypeUuid
} from '../products/finders';

// parsers
import { parseBrandsFromSprout } from 'products/productsParser';
import { getSubstanceTypesByUuidForApplicationType } from './fertility-selectors';
import { getCurrentPath, isEditRoute } from '../routes/selectors';
import { updatePlantingPlanParams } from '../planting-plan-form';
import { getPlantingPlanParams } from '../planting-plan-form/selectors';
import { protectionApplicationTypes } from '../constants/activities';

// Action Types
export const typePrefix = 'vra/products/';
export const actions = {
  SET_ALL_BRANDS_FOR_CROP: `${typePrefix}SET_ALL_BRANDS_FOR_CROP`,
  SET_APPLICATION_PRODUCTS: `${typePrefix}SET_APPLICATION_PRODUCTS`,
  SET_FERTILITY_CHEMICAL_TYPES: `${typePrefix}SET_FERTILITY_CHEMICAL_TYPES`,
  SET_FERTILIZER_SUBSTANCE_TYPES: `${typePrefix}SET_FERTILIZER_SUBSTANCE_TYPES`
};
// Reducer

export default function reducer(
  state = {
    [applicationTypes.SEED]: {
      productsById: {},
      brandsByCrop: {}
    },
    [applicationTypes.FERTILIZER]: {
      productsById: {},
      substanceTypes: {}
    },
    [applicationTypes.APPLICATION]: {
      productsByChemicalTypeUuid: {},
      productsById: {},
      substanceTypes: {}
    },
    [applicationTypes.STABILIZER]: {}
  },
  action = {}
) {
  switch (action.type) {
    case actions.SET_ALL_BRANDS_FOR_CROP:
      return {
        ...state,
        [applicationTypes.SEED]: {
          ...state[applicationTypes.SEED],
          brandsByCrop: {
            ...state[applicationTypes.SEED].brandsByCrop,
            [action.payload.cropUuid]: action.payload.brands
          }
        }
      };

    case actions.SET_APPLICATION_PRODUCTS:
      return {
        ...state,
        [action.payload.applicationType]: {
          ...state[action.payload.applicationType],
          productsById: {
            ...state[action.payload.applicationType].productsById,
            ...action.payload.productsById
          },
          productsBySubstanceTypeUuid: {
            ...state[action.payload.applicationType]
            .productsBySubstanceTypeUuid,
            ...action.payload.productsBySubstanceTypeUuid
          },
          productsByChemicalTypeUuid: {
            ...state[action.payload.applicationType].productsByChemicalTypeUuid,
            ...action.payload.productsByChemicalTypeUuid
          }
        }
      };

    case actions.SET_FERTILIZER_SUBSTANCE_TYPES:
      return {
        ...state,
        [applicationTypes.FERTILIZER]: {
          ...state[applicationTypes.FERTILIZER],
          substanceTypes: action.payload.substanceTypes
        }
      };

    case actions.SET_FERTILITY_CHEMICAL_TYPES:
      return {
        ...state,
        [applicationTypes.FERTILIZER]: {
          ...state[applicationTypes.FERTILIZER],
          chemicalTypeUuid: get(
            action.payload,
            `chemicalTypes.${applicationTypes.FERTILIZER}.chemicalTypeUuid`
          )
        },
        [applicationTypes.STABILIZER]: {
          ...state[applicationTypes.STABILIZER],
          chemicalTypeUuid: get(
            action.payload,
            `chemicalTypes.${applicationTypes.STABILIZER}.chemicalTypeUuid`
          )
        },
        [applicationTypes.APPLICATION]: {
          ...state[applicationTypes.APPLICATION],
          substanceTypes: {
            byName: {
              [applicationSubTypes.FUNGICIDE]: get(
                action.payload,
                `chemicalTypes.${applicationSubTypes.FUNGICIDE}.chemicalTypeUuid`
              ),
              [applicationSubTypes.HERBICIDE]: get(
                action.payload,
                `chemicalTypes.${applicationSubTypes.HERBICIDE}.chemicalTypeUuid`
              ),
              [applicationSubTypes.INSECTICIDE]: get(
                action.payload,
                `chemicalTypes.${applicationSubTypes.INSECTICIDE}.chemicalTypeUuid`
              ),
              [applicationSubTypes.GROWTHREGULATOR]: get(
                action.payload,
                `chemicalTypes.${applicationSubTypes.GROWTHREGULATOR}.chemicalTypeUuid`
              )
            },
            byUuid: {
              [get(
                action.payload,
                `chemicalTypes.${applicationSubTypes.FUNGICIDE}.chemicalTypeUuid`
              )]: applicationSubTypes.FUNGICIDE,
              [get(
                action.payload,
                `chemicalTypes.${applicationSubTypes.HERBICIDE}.chemicalTypeUuid`
              )]: applicationSubTypes.HERBICIDE,
              [get(
                action.payload,
                `chemicalTypes.${applicationSubTypes.INSECTICIDE}.chemicalTypeUuid`
              )]: applicationSubTypes.INSECTICIDE,
              [get(
                action.payload,
                `chemicalTypes.${applicationSubTypes.GROWTHREGULATOR}.chemicalTypeUuid`
              )]: applicationSubTypes.GROWTHREGULATOR
            }
          }
        }
      };

    default:
      return state;
  }
}

// Action Creators
export const setApplicationProducts = ({
  applicationType,
  products,
  substanceTypeUuid,
  chemicalTypeUuid
}) => {
  let productsById = products.reduce((result, product) => {
    const { id } = product;
    if (id) {
      result[id] = product;
    }
    return result;
  }, {});

  // if a substance type exists, group the products that show up as a result for this
  // substance type for accessing later
  let productsBySubstanceTypeUuid = {};
  if (substanceTypeUuid) {
    productsBySubstanceTypeUuid[substanceTypeUuid] = products;
  }

  const productsByChemicalTypeUuid = chemicalTypeUuid
    ? {
      [chemicalTypeUuid]: products
    }
    : {};

  return {
    type: actions.SET_APPLICATION_PRODUCTS,
    payload: {
      applicationType,
      productsById,
      productsBySubstanceTypeUuid,
      productsByChemicalTypeUuid
    }
  };
};

export const setAllBrandsForCrop = ({ brands, cropUuid }) => {
  return {
    type: actions.SET_ALL_BRANDS_FOR_CROP,
    payload: { brands, cropUuid }
  };
};

// TODO: rewrite this to finders/processors/parsers
export const fetchBrandsByActiveCrop = () => {
  return (dispatch, getState) => {
    const state = getState();
    const userId = getActiveUserId(state);
    const cropUuid = getActiveCropDfpUuid(state);
    const fieldCountryCode = getActiveFieldCountryCode(state);

    // don't do a fetch if these are already in state
    if (!get(state, `products.seed.brandsByCrop.${cropUuid}`)) {
      // get brands for this script's crop type
      return fetchBrandsFromSprout({ cropUuid, userId, fieldCountryCode })
      .then(parseBrandsFromSprout)
      .then((brands) => {
        dispatch(setAllBrandsForCrop({ brands, cropUuid }));
      })
      .catch(
        treatErrorAs({
          severity: errorLevel.WARN,
          message: 'there was an error fetching brands',
          andHandleNow: true
        })
      );
    }

    return Promise.resolve();
  };
};

export const fetchFertilizerProducts = ({ substanceTypeUuid }) => {
  return (dispatch, getState) => {
    findFertilizersForSubstanceTypeUuid({
      dispatch,
      getState,
      substanceTypeUuid
    });
  };
};

export const fetchCropProtectionProducts = ({ chemicalTypeIds }) => {
  return (dispatch, getState) => {
    findProtectionsForChemicalTypeUuid({ dispatch, getState, chemicalTypeIds });
  };
};

/**
 * @param {string} currentProductId
 * @param {string} selectId
 */
export const selectChemical = ({ currentProductId, selectId }, clearExistingProducts) => {
  return (dispatch, getState) => {
    const state = getState();
    const isEdit = isEditRoute(getCurrentPath(state));
    const scriptType = getActiveScriptType(state);

    if (isEdit) {
      if (scriptType === scriptTypes.APPLICATION) {
        dispatch(
          setProtectionProductForScript({ productId: currentProductId })
        );
        dispatch(setProductsForScript({
          products: {[applicationTypes.APPLICATIONV2]: currentProductId }
        }, clearExistingProducts));
      } else if (scriptType === scriptTypes.FERTILITY) {
        dispatch(
          setFertilizerProductInput({
            currentProductId,
            selectId
          })
        );

        dispatch(setFertilizerProductForScript({ currentProductId }));
      }
    } else {
      const isApplication = scriptType === scriptTypes.APPLICATION;
      const prodType 
        = isApplication 
          ? scriptTypes.APPLICATION 
          : applicationTypes.FERTILIZER;
      if(isApplication) {
        dispatch(updatePlantingPlanParams({
          products: {[applicationTypes.APPLICATIONV2]: currentProductId }
        }));
      }else{
        dispatch(updatePlantingPlanParams({
          products: {[prodType]: {[selectId]: currentProductId} }
        }));
      }
    }

  };
};

/**
 * Creates a custom chemical before selecting it
 * @param {string} chemicalName
 * @param {string} chemicalTypeId
 * @param {string} selectId
 * @param {string} substanceId
 */
export const selectCustomChemical = ({
  chemicalName,
  chemicalTypeId,
  specificMatterState,
  selectId,
  substanceId,
  isTankMix
}) => {
  return (dispatch, getState) => {
    // NOSONAR False-Positive Case statement
    const state = getState();
    const userUuid = getUserDetails(state).uuid;
    const userId = getActiveUserId(state);
    const scriptType = getActiveScriptType(state);
    const isEdit = isEditRoute(getCurrentPath(state));

    let products;
    let matterState;
    let specificGravity;
    switch (scriptType) {
      case scriptTypes.FERTILITY:
        products
          = state.products[applicationTypes.FERTILIZER]
          .productsBySubstanceTypeUuid[substanceId] || [];
        matterState = specificMatterState ? specificMatterState : 'liquid';
        specificGravity = matterState === 'liquid' ? 1.0 : undefined;
        break;
      case scriptTypes.APPLICATION:
        const productsByUuid = get(
          state,
          `products[${applicationTypes.APPLICATION}].productsByChemicalTypeUuid`
        );
        products = get(productsByUuid, chemicalTypeId);
        break;
      default:
        break;
    }

    const substanceName = getSubstanceTypesByUuidForApplicationType(
      state,
      applicationTypes.FERTILIZER
    )[substanceId];
    const substancesCodes = {
      lime: 'ca',
      nitrogen: 'n',
      phosphorus: 'p',
      potassium: 'k'
    };
    let code = substancesCodes[substanceName];

    sendCreateCustomChemical({
      userId,
      userUuid,
      chemicalName,
      chemicalTypeId: chemicalTypeId,
      matterState,
      specificGravity,
      substances:
        scriptType === scriptTypes.APPLICATION
          ? []
          : [
            {
              id: substanceId,
              code,
              percentage: 100
            }
          ]
    }).then((response) => {
      if (scriptType === scriptTypes.APPLICATION) {
        dispatch(
          setApplicationProducts({
            applicationType: applicationTypes.APPLICATION,
            products: [...products, ...response.results].sort((a, b) =>
              a.name.localeCompare(b.name)
            ),
            chemicalTypeUuid: chemicalTypeId
          })
        );
        dispatch(
          setProductInput({
            active: true,
            applicationType: applicationTypes.APPLICATION,
            currentProductId: response.results[0].id,
            products: response.results,
            selectId: 'select-1'
          })
        );

        if (!isEdit) {
          let currentParams = getPlantingPlanParams(state);
          let existingProducts = currentParams.products[applicationTypes.APPLICATIONV2];

          if (!isEmpty(existingProducts)) {
            dispatch(updatePlantingPlanParams({
              products: {[applicationTypes.APPLICATIONV2]: [...existingProducts, response.results[0].id] },
              activityType: protectionApplicationTypes.APPLICATION_MIX,
              activityCategoryUuid: applicationSubTypes.MIX
            }));
          }
        }
      } else {
        dispatch(
          setApplicationProducts({
            products: [...products, ...response.results].sort((a, b) =>
              a.name.localeCompare(b.name)
            ),
            substanceTypeUuid: substanceId,
            applicationType: applicationTypes.FERTILIZER
          })
        );
      }
      dispatch(
        selectChemical({
          currentProductId: response.results[0].id,
          selectId,
          isCustom: true
        }, !isTankMix)
      );
    });
  };
};

export const setFertilityChemicalTypes = (chemicalTypes) => {
  return {
    type: actions.SET_FERTILITY_CHEMICAL_TYPES,
    payload: { chemicalTypes }
  };
};

export const setFertilizerSubstanceTypes = (substanceTypes) => {
  return {
    type: actions.SET_FERTILIZER_SUBSTANCE_TYPES,
    payload: { substanceTypes }
  };
};
